import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import { getCriteriaTrendMovementThreshold } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

/**
 * Calculates the delta (newest - oldest) from a sparkline array.
 *
 * @param trend Daily values in chronological order, ending at the most recent
 *   day. `null` entries (days with no data) are ignored; the delta spans the
 *   first and last days that actually have data.
 * @returns The difference between the newest and oldest non-null value, or 0
 *   if fewer than two days have data.
 */
export function computeTrendDelta(trend: readonly (number | null)[]): number {
  const values = trend.filter((value): value is number => value !== null);
  const newest = values[values.length - 1];
  const oldest = values[0];
  if (values.length < 2 || newest === undefined || oldest === undefined) {
    return 0;
  }
  return newest - oldest;
}

function formatTimePeriod(days: number): string {
  if (days === 7) {
    return "this week";
  }
  return `last ${days}d`;
}

interface MetricTrendDeltaLabelProps {
  delta: number;
  /**
   * Formats the non-negative magnitude of the delta; this component adds the
   * sign prefix itself.
   */
  formatMagnitude: (magnitude: number) => string;
  /** Absolute delta below which the trend reads as "Flat". */
  flatThreshold: number;
  /** When true, a decreasing value is an improvement (colors a negative delta green). */
  isLowerBetter?: boolean;
  /** Number of days the trend covers (from the queried date range). */
  trendDays: number;
  /** When true, uses neutral gray color instead of directional green/red. */
  useNeutralColor?: boolean;
  /** Size of the label. SMALL shows only delta with tooltip, MEDIUM shows full text. */
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
}

/**
 * Displays a text-based trend indicator like "Flat last 28d" or "+4% last 28d".
 * Formatting, the flat threshold, and the improvement direction are supplied
 * explicitly so it works for any metric scale (rates, scores, durations, …).
 */
export const MetricTrendDeltaLabel: FC<MetricTrendDeltaLabelProps> = ({
  delta,
  formatMagnitude,
  flatThreshold,
  isLowerBetter = false,
  trendDays,
  useNeutralColor = false,
  size = Size.MEDIUM,
}) => {
  const timePeriodText = formatTimePeriod(trendDays);

  const isFlat = Math.abs(delta) < flatThreshold;

  // Positive delta improves by default; `isLowerBetter` flips that.
  const improves = isLowerBetter ? delta < 0 : delta > 0;

  const colorClass =
    useNeutralColor || isFlat
      ? "text-muted-foreground"
      : improves
        ? "text-success"
        : "text-destructive";

  const deltaText = isFlat
    ? "Flat"
    : `${delta >= 0 ? "+" : "-"}${formatMagnitude(Math.abs(delta))}`;

  const label = (
    <span
      className={twMerge("text-xs font-medium whitespace-nowrap", colorClass)}
    >
      {deltaText}
      {size !== Size.SMALL && (
        <span className="text-muted-foreground"> {timePeriodText}</span>
      )}
    </span>
  );

  if (size === Size.SMALL) {
    return (
      <TooltipTrigger>
        <TooltipHoverTarget>{label}</TooltipHoverTarget>
        <SimpleTooltip>
          {deltaText} {timePeriodText}
        </SimpleTooltip>
      </TooltipTrigger>
    );
  }

  return label;
};

interface TrendDeltaLabelProps {
  delta: number;
  gradingValueType: PlaybookCriterionNode["grading_value_type"];
  /** Number of days the trend covers. */
  trendDays: number;
  /** When true, uses neutral gray color instead of directional green/red */
  useNeutralColor?: boolean;
  /** Size of the label. SMALL shows only delta with tooltip, MEDIUM shows full text. */
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
}

/**
 * Trend delta label for a criteria-graded metric, where higher is always
 * better. Derives the magnitude formatting and flat threshold from
 * `gradingValueType`.
 */
export const TrendDeltaLabel: FC<TrendDeltaLabelProps> = ({
  delta,
  gradingValueType,
  trendDays,
  useNeutralColor,
  size,
}) => (
  <MetricTrendDeltaLabel
    delta={delta}
    formatMagnitude={
      gradingValueType === "yes_no"
        ? (magnitude) => `${Math.round(magnitude * 100)}%`
        : (magnitude) => magnitude.toFixed(1)
    }
    flatThreshold={getCriteriaTrendMovementThreshold(gradingValueType)}
    trendDays={trendDays}
    useNeutralColor={useNeutralColor}
    size={size}
  />
);
